import {dsDtos, dsEnums} from '@vectronic/openApi-customer'
import {Error} from './error'

export class Parse {
  static do(dto: dsDtos.Parameter, data: Buffer): string | number | null {
    if (!this.isConfigured(dto.value!, data)) return null
    const offset = dto.value === 'TIME_ZONE' ? -128 : (dto.offset ?? 0)
    const factor = dto.factor ?? 1
    switch (dto.value) {
      case 'TIME_ZONE':
      case 'UNSIGNED_INT':
      case 'TIME':
      case 'DAYTIME':
      case 'DATE_TIME':
      case 'TIME_SPAN':
      case 'RANGE':
      case 'MESSAGE_MODE':
      case 'FLAGS':
      case 'ENUMERATION':
      case 'BAND_FLAGS':
        return (data.readUIntLE(0, data.length) + offset) * factor

      case 'SIGNED_INT':
        return (data.readIntLE(0, data.length) + offset) * factor

      case 'STRING':
      case 'PHONE':
        return data.toString('utf8').replace(/(?<!\0)\0+$/, '')

      case 'FLOAT':
        return (data.readFloatLE() + offset) * factor

      case 'DATE':
        return this.date(data)

      case 'DATE_TIME_AND_UNSIGNED_INT':
        const dateTime = data.readUInt32LE()
        const uInt = (data.readUIntLE(4, data.length - 4) + offset) * factor
        return `${dateTime},${uInt}`

      case 'VERSION_1_1_1_1':
        return this.version(data, 1, 1, 1, 1)

      case 'VERSION_2_2':
        return this.version(data, 2, 2)

      case 'VERSION_2_1_1':
        return this.version(data, 2, 1, 1)

      case 'DATE_AND_VERSION_1_1_1':
        const parsedDate = this.date(data.subarray(0, 3))
        const parsedVersion = this.version(data.subarray(3, 6).reverse(), 1, 1, 1)
        return `${parsedDate},${parsedVersion}`

      case 'HEX':
        return this.hexArray(data)

      case 'HEX_DOT':
        return this.hexArray(data, '.')

      case 'VHF_PATTERN':
        return `${data[0]},${data[1]},${data[2] * 50}`

      case 'CALIBRATION_BIAS':
        throw `${Error.NOT_IMPLEMENTED}: CALIBRATION_BIAS.` //TODO

      case 'CALIBRATION_MATRIX':
        throw `${Error.NOT_IMPLEMENTED}: CALIBRATION_MATRIX.` //TODO

      case 'IP_ADDRESS':
        return this.version(data, 1, 1, 1, 1)

      case 'LIST_UNSIGNED_INT_4':
        return this.list(data, 4, (d) => d.readUIntLE(0, 4))

      case 'LIST_UNSIGNED_INT_2':
        return this.list(data, 2, (d) => d.readUIntLE(0, 2))

      case 'LIST_DATE_TIME_AND_HEX_8':
        return this.list(data, 8, (d) => `${d.subarray(0, 4).readUInt32LE()},${this.hexArray(d.subarray(4, 8))}`)

      case 'GLOBALSTAR_ESN':
        return data.readInt8(3) + '-' + data.readUIntLE(0, 3)

      case null:
      case undefined:
        throw `${Error.NO_VALUETYPE}, possibly obsolete Parameter. ${JSON.stringify(dto)}`

      default:
        throw `${Error.UNKNOWN_VALUETYPE}: ${dto.value}.`
    }
  }

  static isConfigured(valueType: dsEnums.Value, data: Buffer) {
    if (valueType === 'FLAGS') return true
    if (valueType === 'SIGNED_INT') return true
    for (const d of data) if (d != 255) return true
    return false
  }

  private static list(data: Buffer, itemSize: number, f: (dataSub: Buffer) => any) {
    let index = 0
    const list: any = []
    let rest = data.subarray(index)
    while (index != data.length && !rest.every((byte) => byte === 0xff)) {
      const subarray = data.subarray(index, index + itemSize)
      list.push(f(subarray))
      index += itemSize
      rest = data.subarray(index)
    }
    return list.toString()
  }

  private static date(data: Buffer) {
    const MILLISECS_IN_A_DAY = 1000 * 60 * 60 * 24
    const daysSince2000 = Math.round(
      (Date.UTC(2000 + data[0], data[1] - 1, data[2]) - Date.UTC(2000, 0)) / MILLISECS_IN_A_DAY,
    )
    return daysSince2000
  }

  private static version(data: Buffer, ...byteDistribution: number[]) {
    let version = ''
    let start = data.length
    for (const bytes of byteDistribution) {
      start -= bytes
      version += data.readUIntLE(start, bytes) + (start === 0 ? '' : '.')
    }
    return version
  }

  private static hexArray(data: Buffer, joinString?: string) {
    const hexArray: string[] = []
    data.forEach((d) => {
      let hex = d.toString(16)
      if (hex.length === 1) hex = '0' + hex
      hexArray.push(hex)
    })
    return hexArray.join(joinString ?? '')
  }
}
